import { Box, HStack, Text, Icon, Wrap, WrapItem, Tag, TagLabel } from '@chakra-ui/react'
import { FiSearch, FiFile } from 'react-icons/fi'
import { SearchFileSuggestion } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'

export type SuggestionItem =
  | { type: 'query'; value: string }
  | { type: 'file'; value: SearchFileSuggestion }
  | { type: 'tag'; value: string }

interface SearchSuggestionsDropdownProps {
  query: string
  items: SuggestionItem[]
  activeIndex: number
  onSelect: (item: SuggestionItem) => void
  onHover: (index: number) => void
}

export default function SearchSuggestionsDropdown({
  query,
  items,
  activeIndex,
  onSelect,
  onHover,
}: SearchSuggestionsDropdownProps) {
  const { t } = useAppTranslation('search')

  const fileItems = items.filter((item) => item.type === 'file')
  const tagItems = items.filter((item) => item.type === 'tag')

  const rowProps = (index: number) => ({
    role: 'option',
    'aria-selected': index === activeIndex,
    bg: index === activeIndex ? 'gray.100' : 'transparent',
    cursor: 'pointer',
    onMouseEnter: () => onHover(index),
    // mousedown fires before the input's blur, so selection wins over close
    onMouseDown: (e: React.MouseEvent) => {
      e.preventDefault()
      onSelect(items[index])
    },
  })

  return (
    <Box
      position="absolute"
      top="calc(100% + 4px)"
      left={0}
      right={0}
      zIndex={20}
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      boxShadow="xl"
      py={2}
      role="listbox"
      overflow="hidden"
    >
      {/* "Search for ..." row is always item 0 */}
      <HStack px={4} py={2} spacing={3} {...rowProps(0)}>
        <Icon as={FiSearch} color="gray.400" boxSize="16px" />
        <Text fontSize="sm" color="gray.700" noOfLines={1}>
          {t('suggestions.searchFor', { query })}
        </Text>
      </HStack>

      {fileItems.length > 0 && (
        <>
          <Text px={4} pt={2} pb={1} fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
            {t('suggestions.files')}
          </Text>
          {fileItems.map((item) => {
            const index = items.indexOf(item)
            const file = item.value as SearchFileSuggestion
            const label = file.display_name || file.original_name || file.name
            return (
              <HStack key={`file-${file.id}`} px={4} py={2} spacing={3} {...rowProps(index)}>
                <Icon as={FiFile} color="brand.500" boxSize="16px" flexShrink={0} />
                <Text fontSize="sm" color="gray.700" noOfLines={1}>
                  {label}
                </Text>
              </HStack>
            )
          })}
        </>
      )}

      {tagItems.length > 0 && (
        <>
          <Text px={4} pt={2} pb={1} fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
            {t('suggestions.tags')}
          </Text>
          <Wrap px={4} py={2} spacing={2}>
            {tagItems.map((item) => {
              const index = items.indexOf(item)
              const tag = item.value as string
              return (
                <WrapItem key={`tag-${tag}`}>
                  <Tag
                    size="sm"
                    variant={index === activeIndex ? 'solid' : 'subtle'}
                    colorScheme="blue"
                    borderRadius="full"
                    cursor="pointer"
                    role="option"
                    aria-selected={index === activeIndex}
                    onMouseEnter={() => onHover(index)}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      onSelect(item)
                    }}
                  >
                    <TagLabel>#{tag}</TagLabel>
                  </Tag>
                </WrapItem>
              )
            })}
          </Wrap>
        </>
      )}
    </Box>
  )
}
